import { memo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, CheckCircle, MapPin, Flag, Ban, Video } from "lucide-react";
import { Profile } from "@/types/profile";
import ProfileVideoCarousel from "@/components/profile/ProfileVideoCarousel";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import RequestPortalButton from "@/components/portal/RequestPortalButton";

interface ProfileModalProps {
  profile: Profile;
  isOpen: boolean;
  onClose: () => void;
  onReport: () => void;
  onBlock: () => void;
}

const ProfileModal = memo(({ profile, isOpen, onClose, onReport, onBlock }: ProfileModalProps) => {
  const { t } = useTranslation();
  
  // Get display name: username if available, otherwise first name
  const getDisplayName = () => {
    const p = displayProfile;
    if (p.username) return p.username;
    if (p.name) return p.name.split(' ')[0];
    return 'User';
  };

  const { user } = useAuthContext();
  
  // Helper to format enum values for display
  const formatEnum = (value: string | undefined) => {
    if (!value) return "";
    return value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  const [isMatched, setIsMatched] = useState(false);
  const [showVideos, setShowVideos] = useState(false);
  const [fullProfile, setFullProfile] = useState<Profile | null>(null);
  
  // Fetch full profile data with all fields when modal opens
  useEffect(() => {
    const fetchFullProfile = async () => {
      if (!isOpen || !profile.id) return;
      
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", profile.id)
        .single();
      
      if (!error && data) {
        // Transform to Profile type
        const transformedProfile: Profile = {
          ...profile,
          country: (data as any).country || undefined,
          nationality: (data as any).nationality || undefined,
          maritalStatus: (data as any).marital_status || undefined,
          education: (data as any).education || undefined,
          profession: (data as any).profession || undefined,
          annualIncome: (data as any).annual_income || undefined,
          socioeconomicStatus: (data as any).socioeconomic_status || undefined,
          lookingFor: (data as any).looking_for || undefined,
          willingToRelocate: (data as any).willing_to_relocate || undefined,
          children: (data as any).children || undefined,
        };
        setFullProfile(transformedProfile);
      } else {
        setFullProfile(profile);
      }
    };
    
    if (isOpen) {
      fetchFullProfile();
    }
  }, [isOpen, profile.id]);
  
  // Use fullProfile if available, otherwise fall back to profile prop
  const displayProfile = fullProfile || profile;

  // Check if we have a mutual match with this profile
  useEffect(() => {
    const checkMatch = async () => {
      if (!user || !profile.id) return;

      // Check for mutual pops (match)
      const { data } = await supabase
        .from("pops")
        .select("id")
        .eq("popper_id", displayProfile.id)
        .eq("popped_id", user.id)
        .maybeSingle();

      // Also check if we've popped them
      const { data: ourPop } = await supabase
        .from("pops")
        .select("id")
        .eq("popper_id", user.id)
        .eq("popped_id", displayProfile.id)
        .maybeSingle();

      setIsMatched(!!(data && ourPop));
    };

    if (isOpen) {
      checkMatch();
    }
  }, [isOpen, user, profile.id]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-scale-in"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-sm mx-4 bg-card rounded-3xl overflow-hidden shadow-2xl animate-slide-up max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/30 flex items-center justify-center text-white hover:bg-black/50 transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Toggle between photo and video view */}
        {showVideos ? (
          <div className="relative">
            <ProfileVideoCarousel 
              userId={displayProfile.id} 
              isMatched={isMatched}
              userName={getDisplayName()}
            />
            <button
              onClick={() => setShowVideos(false)}
              className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-black/50 text-white text-xs font-medium hover:bg-black/70 transition-colors"
            >
              View Photos
            </button>
          </div>
        ) : (
          <>
            {/* Profile photo header */}
            <div className="relative h-64">
              <img
                src={displayProfile.photos[0]}
                alt={displayProfile.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              
              {/* Name overlay */}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-bold text-white">
                    {getDisplayName()}, {displayProfile.age}
                  </h2>
                  {displayProfile.isVerified && (
                    <CheckCircle className="w-6 h-6 text-primary fill-primary-foreground" />
                  )}
                </div>
                
                {/* Distance */}
                <div className="flex items-center gap-1 mt-1 text-white/80 text-sm">
                  <MapPin className="w-4 h-4" />
                  <span>{displayProfile.distance}</span>
                </div>
              </div>

              {/* Watch videos button */}
              {displayProfile.hasVideo && (
                <button
                  onClick={() => setShowVideos(true)}
                  className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-black/50 text-white text-xs font-medium hover:bg-black/70 transition-colors flex items-center gap-1.5"
                >
                  <Video className="w-3.5 h-3.5" />
                  Watch Videos
                </button>
              )}
            </div>
          </>
        )}

        {/* Profile content */}
        <div className="p-5 space-y-6">
          {/* Basic Info */}
          <div>
            <h3 className="text-lg font-semibold mb-3">{t("profile.basicInfo")}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">{t("profile.name")}:</span>
                <span className="font-medium">{getDisplayName()}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">{t("profile.age")}:</span>
                <span className="font-medium">{profile.age}</span>
              </div>
              {profile.distance && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">{t("profile.location")}:</span>
                  <span className="font-medium">{profile.distance}</span>
                </div>
              )}
            </div>
          </div>

          {/* Bio */}
          {displayProfile.bio && (
            <div>
              <h3 className="text-lg font-semibold mb-2">{t("profile.about")}</h3>
              <p className="text-foreground text-base leading-relaxed">
                {displayProfile.bio}
              </p>
            </div>
          )}

          {/* Personal Information - Always show section header, show fields that have values */}
          <div>
            <h3 className="text-lg font-semibold mb-3">{t("profile.personalInformation")}</h3>
            <div className="space-y-2 text-sm">
              {displayProfile.country ? (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[100px]">{t("profile.country")}:</span>
                  <span className="font-medium flex-1 break-words">{displayProfile.country}</span>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[100px]">{t("profile.country")}:</span>
                  <span className="font-medium flex-1 break-words text-muted-foreground">—</span>
                </div>
              )}
              {displayProfile.nationality ? (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[100px]">{t("profile.nationality")}:</span>
                  <span className="font-medium flex-1 break-words">{displayProfile.nationality}</span>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[100px]">{t("profile.nationality")}:</span>
                  <span className="font-medium flex-1 break-words text-muted-foreground">—</span>
                </div>
              )}
              {displayProfile.maritalStatus ? (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[100px]">{t("profile.maritalStatus")}:</span>
                  <span className="font-medium flex-1 break-words">{t(`profile.${displayProfile.maritalStatus}`) || formatEnum(displayProfile.maritalStatus)}</span>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[100px]">{t("profile.maritalStatus")}:</span>
                  <span className="font-medium flex-1 break-words text-muted-foreground">—</span>
                </div>
              )}
              {displayProfile.religion ? (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[100px]">{t("profile.religion")}:</span>
                  <span className="font-medium flex-1 break-words">{displayProfile.religion}</span>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[100px]">{t("profile.religion")}:</span>
                  <span className="font-medium flex-1 break-words text-muted-foreground">—</span>
                </div>
              )}
              {displayProfile.education ? (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[100px]">{t("profile.education")}:</span>
                  <span className="font-medium flex-1 break-words">{displayProfile.education}</span>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[100px]">{t("profile.education")}:</span>
                  <span className="font-medium flex-1 break-words text-muted-foreground">—</span>
                </div>
              )}
              {displayProfile.profession ? (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[100px]">{t("profile.profession")}:</span>
                  <span className="font-medium flex-1 break-words">{displayProfile.profession}</span>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[100px]">{t("profile.profession")}:</span>
                  <span className="font-medium flex-1 break-words text-muted-foreground">—</span>
                </div>
              )}
              {displayProfile.annualIncome ? (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[100px]">{t("profile.annualIncome")}:</span>
                  <span className="font-medium flex-1 break-words">{displayProfile.annualIncome}</span>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[100px]">{t("profile.annualIncome")}:</span>
                  <span className="font-medium flex-1 break-words text-muted-foreground">—</span>
                </div>
              )}
              {displayProfile.socioeconomicStatus ? (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[100px]">{t("profile.socioeconomicStatus")}:</span>
                  <span className="font-medium flex-1 break-words">{t(`profile.${displayProfile.socioeconomicStatus}`) || formatEnum(displayProfile.socioeconomicStatus)}</span>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[100px]">{t("profile.socioeconomicStatus")}:</span>
                  <span className="font-medium flex-1 break-words text-muted-foreground">—</span>
                </div>
              )}
            </div>
          </div>

          {/* Lifestyle & Intent - Always show section header, show fields that have values */}
          <div>
            <h3 className="text-lg font-semibold mb-3">{t("profile.lifestyleAndIntent")}</h3>
            <div className="space-y-2 text-sm">
              {displayProfile.lookingFor ? (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[120px]">{t("profile.lookingFor")}:</span>
                  <span className="font-medium flex-1 break-words">{t(`profile.${displayProfile.lookingFor}`) || formatEnum(displayProfile.lookingFor)}</span>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[120px]">{t("profile.lookingFor")}:</span>
                  <span className="font-medium flex-1 break-words text-muted-foreground">—</span>
                </div>
              )}
              {displayProfile.willingToRelocate ? (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[120px]">{t("profile.willingToRelocate")}:</span>
                  <span className="font-medium flex-1 break-words">{t(`profile.${displayProfile.willingToRelocate}`) || formatEnum(displayProfile.willingToRelocate)}</span>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[120px]">{t("profile.willingToRelocate")}:</span>
                  <span className="font-medium flex-1 break-words text-muted-foreground">—</span>
                </div>
              )}
              {displayProfile.children ? (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[120px]">{t("profile.children")}:</span>
                  <span className="font-medium flex-1 break-words">{t(`profile.${displayProfile.children}`) || formatEnum(displayProfile.children)}</span>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground min-w-[120px]">{t("profile.children")}:</span>
                  <span className="font-medium flex-1 break-words text-muted-foreground">—</span>
                </div>
              )}
            </div>
          </div>

          {/* Match status badge */}
          {isMatched && (
            <div className="px-3 py-2 rounded-xl bg-green-500/10 border border-green-500/20 text-center">
              <span className="text-green-600 text-sm font-medium">
                🎉 You're matched! All videos are unlocked
              </span>
            </div>
          )}

          {/* Portal Button */}
          <div className="mt-4">
            <RequestPortalButton
              userId={displayProfile.id}
              userName={getDisplayName()}
              className="w-full"
            />
          </div>

          {/* Badges */}
          {displayProfile.badges.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {displayProfile.badges.map((badge, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium"
                >
                  {badge}
                </span>
              ))}
            </div>
          )}

          {/* Safety actions */}
          <div className="flex items-center justify-center gap-4 mt-6 pt-4 border-t border-border">
            <button
              onClick={onReport}
              className="flex items-center gap-2 px-4 py-2 text-muted-foreground hover:text-destructive transition-colors text-sm"
            >
              <Flag className="w-4 h-4" />
              Report
            </button>
            <button
              onClick={onBlock}
              className="flex items-center gap-2 px-4 py-2 text-muted-foreground hover:text-destructive transition-colors text-sm"
            >
              <Ban className="w-4 h-4" />
              Block
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

ProfileModal.displayName = "ProfileModal";

export default ProfileModal;
